import { T, tt, useAuth } from '@agos/shared'
import { PageHeader, TabGroup } from '@agos/ui'
import { History, KeyRound, Network, Shield, Users, UserSearch } from 'lucide-react'
import { useSearchParams } from 'wouter'

import { useRolesOverview } from '../roles/roles-api'
import { DelegationTab } from './delegation/DelegationTab'
import { HistoryTab } from './history/HistoryTab'
import { HoldersTab } from './holders/HoldersTab'
import { PeopleTab } from './people/PeopleTab'
import { RolesTab } from './roles/RolesTab'
import { tabFrom, tabsFor, type Navigate, type PermissionsTab, type Place } from './role-model'

/**
 * `/permissions` - *Role a práva*: who may do what, who may change it, and who changed it. `FRONTEND.md` §9.752
 * (owner O119), on `BACKEND.md` §10A.407 and §10A.408; the design is `docs/designs/O119-custom-roles.md` §2.9.
 *
 * **The owner**: *"make roles much much more advanced ... redesign it somehow more intuitive ... custom roles (who can
 * edit which roles, assign...)"*. So the screen starts from **roles** rather than a matrix, says **who a change
 * reaches** before it is saved, says **which ticks the server checks**, and writes delegation as sentences.
 *
 * **This file is the host and nothing else**: the header, the tabs and the place in the URL. Each tab reads what it
 * needs and fails on its own (§9.543), so a failed audit read never takes the roles list with it.
 *
 * **Who sees what** - the overview's `you.mainAdmin` decides, and the session's role stands in while it is read or when
 * it fails: a main administrator gets *Role · Ľudia · Kto čo smie · Kto spravuje role · História*, a delegate *Role ·
 * Ľudia* scoped to what they manage. The route stays `/permissions`, because route parity pins it, and a place on the
 * screen is `?tab=&role=&person=` so it can be sent to somebody and survives a reload.
 *
 * FB-103's matrix (the third switch state, the side effects of a parent) lives on in the role editor unchanged, and
 * the thirty-two decisions `permissions.spec.ts` records keep their markers.
 */
export function PermissionsPage() {
  const { user } = useAuth()
  const overview = useRolesOverview()
  const [params, setParams] = useSearchParams()

  const mainAdmin = overview.data?.you.mainAdmin ?? user?.systemRole === 'main_admin'
  const tabs = tabsFor(mainAdmin)
  const tab = tabFrom(params.get('tab'), tabs)
  const place: Place = {
    tab,
    role: params.get('role') ?? '',
    person: Number(params.get('person') ?? '') || 0,
  }

  /* In the URL, so a role's editor or a person's panel is something somebody can send a colleague, and so a reload
     does not forget it. A change of tab drops the role and the person, which belong to the tab they were opened on. */
  const navigate: Navigate = (next, options = {}) => {
    const search = new URLSearchParams(params)
    const nextTab = next.tab ?? tab
    if (next.tab !== undefined && next.tab !== tab) {
      search.delete('role')
      search.delete('person')
    }
    search.set('tab', nextTab)
    if (next.role !== undefined) {
      if (next.role === '') search.delete('role')
      else search.set('role', next.role)
    }
    if (next.person !== undefined) {
      if (next.person <= 0) search.delete('person')
      else search.set('person', String(next.person))
    }
    setParams(search, { replace: options.replace === true })
  }

  const labels: Record<PermissionsTab, { label: string; icon: typeof Users }> = {
    roles: { label: tt('Role'), icon: Shield },
    people: { label: tt('Ľudia'), icon: Users },
    holders: { label: tt('Kto čo smie'), icon: UserSearch },
    delegation: { label: tt('Kto spravuje role'), icon: Network },
    history: { label: tt('História'), icon: History },
  }

  return (
    <div className="space-y-5" data-permissions-screen={mainAdmin ? 'main-admin' : 'delegate'}>
      <PageHeader
        title={tt('Role a práva')}
        description={tt('Kto smie čo, kto to smie meniť a kto to zmenil.')}
        icon={<KeyRound className="w-5 h-5" />}
      />

      {/* Legacy's banner, kept (design §1.7): the one fact about rights no tick on this screen can change. */}
      <p
        className="rounded-xl border border-violet-500/30 bg-violet-500/5 px-3 py-2 text-xs text-muted-foreground"
        data-main-admin-note
      >
        <T>Hlavný administrátor má vždy všetky práva - nedá sa obmedziť ani výnimkou.</T>
      </p>

      <TabGroup
        variant="bar"
        tabs={tabs.map((id) => ({ id, label: labels[id].label, icon: labels[id].icon }))}
        active={tab}
        onChange={(id) => navigate({ tab: tabFrom(id, tabs) }, { replace: true })}
      />

      {tab === 'roles' && <RolesTab mainAdmin={mainAdmin} place={place} navigate={navigate} />}
      {tab === 'people' && <PeopleTab mainAdmin={mainAdmin} place={place} navigate={navigate} />}
      {tab === 'holders' && <HoldersTab />}
      {tab === 'delegation' && <DelegationTab />}
      {tab === 'history' && <HistoryTab />}
    </div>
  )
}
